import { FC, useCallback } from 'react';
import { Box, Divider } from '@wix/design-system';
import NoReviews from '@components/Reviews/NoReviews';
import { ReviewsContainerProps } from './types';
import ReviewsWithPagination from './ReviewsWithPagination';
import ReviewsWithInfiniteScroll from './ReviewsWithInfiniteScroll';
import DefaultReviewsHeader from './DefaultRenderedComponents/ReviewsHeader';

const Reviews: FC<ReviewsContainerProps> = (props) => {
  const { content, paginationMode } = props;

  const {
    ReviewsHeader = DefaultReviewsHeader,
    LoadingReviews,
    AddNewReviewBanner,
    loadNewReview,
    enableNoReviews = true,
    i18nKeys,
    reviews,
    isMobileView,
    showReviewsCount,
    title,
    reviewsCount,
    averageReviewRating,
    borderContainerClass,
  } = content;

  const renderPaginatedContent = useCallback(() => {
    if (paginationMode === 'paging') {
      const { pagingProps } = props as Extract<
        typeof props,
        { paginationMode: 'paging' }
      >;
      return <ReviewsWithPagination {...content} {...pagingProps} />;
    } else {
      const { infiniteScrollProps } = props as Extract<
        typeof props,
        { paginationMode: 'infiniteScroll' }
      >;
      return (
        <ReviewsWithInfiniteScroll {...content} {...infiniteScrollProps} />
      );
    }
  }, [content, paginationMode, props]);

  return (
    <Box display="block" dataHook="reviews-section" width="100%">
      <ReviewsHeader
        title={title}
        reviewsCount={reviewsCount}
        averageReviewRating={averageReviewRating}
        isMobileView={isMobileView}
        showReviewsCount={showReviewsCount}
      />
      {AddNewReviewBanner && (
        <Box marginTop="24px">
          <AddNewReviewBanner />
        </Box>
      )}
      {reviews?.length > 0 ? (
        <>
          {isMobileView && showReviewsCount && <Divider />}
          {loadNewReview && <LoadingReviews isInitialLoading={false} />}
          {renderPaginatedContent()}
        </>
      ) : loadNewReview ? (
        <LoadingReviews isInitialLoading={true} />
      ) : (
        enableNoReviews && (
          <NoReviews
            hasNegativeMargin={Boolean(borderContainerClass)}
            noReviewsKey={i18nKeys.noReviews}
            isMobileView={isMobileView}
          />
        )
      )}
    </Box>
  );
};

export default Reviews;
